import { decodeAndDecompress } from '@/modules/xsz';
import { useAuthStore } from '@/stores/auth';
import { useMrxStore } from '@/stores/mrx';
import { usePartnerConfigStore } from '@/stores/partnerConfig';
import { useTestModeStore } from '@/stores/testMode';
import { useTrackStore } from '@/stores/track';
import { sendCollect } from '@/utils/collectUpload';
import { gatherResponses } from '@/utils/panelResponses';

const REQUEST_TIMEOUT_ERROR_MESSAGE = 'Request timed out';
const MRX_URL = '/api/v3/v4/mrx';

interface Deferred extends Promise<unknown> {
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}

const sentry = (): any => (window as any).App?.$sentry;

let loaded: true | Deferred | null = null;

export function mrxLoaded(): true | Deferred {
  if (loaded !== null) return loaded;

  if ((window as any).mrx) {
    loaded = true;
    return loaded;
  }

  let res: (value: unknown) => void;
  let rej: (reason: unknown) => void;
  const promise = new Promise((resolve, reject) => {
    res = resolve;
    rej = reject;
  }) as Deferred;

  promise.resolve = res!;
  promise.reject = rej!;
  promise.catch(() => {
    console.error('MRX Not Loaded');
  });

  loaded = promise;
  return loaded;
}

// Online engagements use the device library once it has loaded and never wait for it.
export function useDeviceMRX(): boolean {
  return usePartnerConfigStore().offline || Boolean((window as any).mrx);
}

export async function conditions(): Promise<any[]> {
  let results: any[] = [];

  if (!useDeviceMRX() && !useAuthStore().authenticated()) {
    return results;
  }

  try {
    if (useDeviceMRX()) {
      await mrxLoaded();
      results = (window as any).mrx.logic.conditions();
    }
    else if (useAuthStore().authenticated()) {
      const mrxConditionsUrl = `${MRX_URL}/conditions`;
      const requestHeaders = {
        'Content-Type': 'application/json',
      };

      const response = await fetch(mrxConditionsUrl, {
        method: 'GET',
        headers: requestHeaders,
      });

      if (response.status === 401) {
        return results;
      }

      if (!response.ok) {
        throw new Error(`Request to ${mrxConditionsUrl} returned not-OK status: ${response.status} - ${response.statusText}`);
      }

      results = await response.json();
    }
  }
  catch (e) {
    sentry()?.withScope((scope: any) => {
      scope.setTag("location", "mrx-condition-fetch")
      sentry().captureException(e)
    });
  }

  return results;
}

export async function convert(payload: any): Promise<any> {
  if (useDeviceMRX()) {
    await mrxLoaded();
    return (window as any).mrx.logic.convert(payload);
  }
  const mrxConvertUrl = `${MRX_URL}/convert`;
  const requestHeaders = {
    'Content-Type': 'application/json',
  };
  const requestBody = JSON.stringify([payload]);

  const response = await fetch(mrxConvertUrl, {
    method: 'POST',
    headers: requestHeaders,
    body: requestBody
  });

  if (!response.ok) {
    throw new Error(`Request to ${mrxConvertUrl} returned not-OK status: ${response.status} - ${response.statusText}`);
  }

  const responseBody = await response.json();
  return responseBody.shift();
}

export async function convertFlexi(payload: any): Promise<any> {
  if (useDeviceMRX()) {
    await mrxLoaded();
    return (window as any).mrx.logic.convertFlexi(payload);
  }

  const mrxConvertUrl = `${MRX_URL}/convertFlexi`;
  const requestHeaders = {
    'Content-Type': 'application/json',
  };
  const requestBody = JSON.stringify([payload]);

  const response = await fetch(mrxConvertUrl, {
    method: 'POST',
    headers: requestHeaders,
    body: requestBody
  });

  if (!response.ok) {
    throw new Error(`Request to ${mrxConvertUrl} returned not-OK status: ${response.status} - ${response.statusText}`);
  }

  const responseBody = await response.json();
  return responseBody.shift();
}

export async function load(): Promise<boolean> {
  const loadedPromise = mrxLoaded();
  let ok = true;
  try {
    if (!(window as any).mrx) {
      ok = false
      let req = await fetch('api/v3/mrx2.js')
      let data = await req.text()
      let query = decodeAndDecompress(data)
      if (query.ok) {
        const runModule = new Function(query.data);
        runModule();
        (loadedPromise as Deferred).resolve(query.ok);
        // a retry can succeed after an earlier attempt rejected the shared promise
        loaded = true;
      }
      else {
        (loadedPromise as Deferred).reject(query.ok);
      }
      ok = query.ok
    }
  }
  catch (e) {
    console.error('Error loading MRX:', e);
    window.addEventListener('online', () => { load() }, { once: true });
  }

  return ok;
}

async function runAssessment(payload: any): Promise<any[]> {
  const testModeStore = useTestModeStore();
  let results: any[] = [];
  useMrxStore().reset();
  let debug = testModeStore.get('logScores')
  let showAll = testModeStore.get('showAllAssessments')
  try {
    if (useDeviceMRX()) {
      await mrxLoaded();
      let assessed = await (window as any).mrx.logic.assess(payload, {
        debug: debug,
        all: showAll
      })

      if (debug) {
        console.log(JSON.parse(JSON.stringify(assessed)))
      }

      let cared = await (window as any).mrx.logic.care(assessed, {
        debug: debug,
        all: showAll
      })

      results = await (window as any).mrx.logic.treat(cared)

      results.push({
        type: 'status',
        name: 'dispatched',
        value: 'device'
      })
    }
    else {
      const assessUrl = `${MRX_URL}/assess?debug=${debug}&all=${showAll}`
      const requestHeaders = {
        'Content-Type': 'application/json',
      }
      const requestBody = JSON.stringify([payload])

      const response = await fetch(assessUrl, {
        method: 'POST',
        headers: requestHeaders,
        body: requestBody
      })

      if (!response.ok) {
        throw new Error(`Request to ${assessUrl} returned not-OK status: ${response.status} - ${response.statusText}`)
      }

      const responseBody = await response.json()
      results = responseBody.shift()

      results.push({
        type: 'status',
        name: 'dispatched',
        value: 'server'
      })
    }
  }
  catch (e) {
    console.log(e)
    throw new Error('Error retrieving assessment')
  }

  results.push({
    type: 'status',
    name: 'mrx',
    value: 2
  })

  return results;
}

export async function assess(payload: any = {}): Promise<any[]> {
  const testModeStore = useTestModeStore();
  const partnerConfigStore = usePartnerConfigStore();
  const mrxStore = useMrxStore();
  let results: any[] = []
  const arrayIsEmpty = (arr: any) => !Array.isArray(arr) || arr.length === 0

  if (mrxStore.ready) {
    try {
      const checkMrxErrors = !testModeStore.get('disableMRXErrorChecking')
      const trackStore = useTrackStore()
      const classifications = trackStore.classifications
      let conditions = trackStore.conditions

      if (payload.conditions) {
        conditions = conditions.filter((condition: any) => payload.conditions.includes(condition.name))
      }

      let responses = await gatherResponses(trackStore.current)

      const responsesIsEmpty = arrayIsEmpty(responses)
      if (checkMrxErrors && responsesIsEmpty) {
        throw new Error('MRX:ERR:1 - Responses did not convert')
      }

      let bundle = responses.concat(classifications, conditions)

      results = await Promise.race([
        runAssessment(bundle),
        new Promise<any[]>(function(resolve, reject) {  //Request timeout after 10sec
          setTimeout(() => reject(new Error(REQUEST_TIMEOUT_ERROR_MESSAGE)), 10000)
        })
      ]).catch((err) => {
        results = []
        if (checkMrxErrors) {
          if (err.message === REQUEST_TIMEOUT_ERROR_MESSAGE) {
            throw new Error('MRX:ERR:2 - Assessment call timed out', { cause: err })
          } else {
            throw new Error('Error during assessment call', { cause: err })
          }
        }
        return results
      })

      if (arrayIsEmpty(results)) {
        results = []
        if (checkMrxErrors) throw new Error('MRX:ERR:3 - Assessment did not return an array')
      }

      const statusTypeResults = results.filter(result => result.type === 'status')
      const performedObject = statusTypeResults.find(result => result.name === 'dispatched')
      const statusObject = statusTypeResults.find(result => result.name === 'clinical_logic')
      const assessmentTypeResults = results.filter(result => result.type === 'assessment')
      const conditionTypeResults = results.filter(result => result.type === 'condition')

      if (checkMrxErrors) {
        if (!performedObject) {
          throw new Error('MRX:ERR:4 - Assessment returned an empty bundle')
        }

        if (!statusObject) {
          throw new Error('MRX:ERR:5 - No status object in bundle')
        } else if (statusObject.value === 'invalid') {
          throw new Error('MRX:ERR:6 - Bundle returned with clinical logic invalid')
        }

        if (assessmentTypeResults.length !== conditionTypeResults.length) {
          throw new Error('MRX:ERR:7 - Not all conditions were assessed')
        }
      }

      if (performedObject!.value === 'server') {
        load()
      }

      results.push({ type: 'status', name: 'validity', value: 'valid' })

      mrxStore.setBundle(results)
    }
    catch (e: any) {
      results.push({ type: 'status', name: 'validity', value: 'invalid' })
      results.push({ type: 'status', name: 'error', value: e.message })
      if (partnerConfigStore.offline) {
        results.push({ type: 'notification', name: 'mrx', value: 'must_refresh' })
      }
      mrxStore.setBundle(results)
    }

  }
  else {
    results = mrxStore.bundleSnapshot
  }

  if (partnerConfigStore.submitOnAssessment && !payload.suppressSubmitOnAssessment) {
    sendCollect()
  }

  if (testModeStore.get('logScores')) {
    let items = mrxStore.assessments()
    console.log(JSON.stringify(items, null, 2))
  }

  return results
}

export async function streamlinedOptions(payload: any): Promise<any> {
  let results: any = {};
  try {
    if (useDeviceMRX()) {
      await mrxLoaded();
      results = await (window as any).mrx.logic.streamlinedOptions(payload)
    }
    else {
      const mrxStreamlinedOptionsUrl = `${MRX_URL}/streamlinedOptions`
      const requestHeaders = {
        'Content-Type': 'application/json',
      }
      const requestBody = JSON.stringify([payload])

      const response = await fetch(mrxStreamlinedOptionsUrl, {
        method: 'POST',
        headers: requestHeaders,
        body: requestBody
      })

      if (!response.ok) {
        throw new Error(`Request to ${mrxStreamlinedOptionsUrl} returned not-OK status: ${response.status} - ${response.statusText}`)
      }

      results = await response.json()
    }
  }
  catch (e) {
    console.log(e)
  }

  return results;
}

export async function streamlinedEvaluation(payload: any): Promise<any> {
  let results: any = {};
  try {
    if (useDeviceMRX()) {
      await mrxLoaded();
      results = await (window as any).mrx.logic.streamlinedEvaluation(payload)
    }
    else {
      const mrxStreamlinedEvaluationUrl = `${MRX_URL}/streamlinedEvaluation`
      const requestHeaders = {
        'Content-Type': 'application/json',
      }
      const requestBody = JSON.stringify([payload])

      const response = await fetch(mrxStreamlinedEvaluationUrl, {
        method: 'POST',
        headers: requestHeaders,
        body: requestBody
      })

      if (!response.ok) {
        throw new Error(`Request to ${mrxStreamlinedEvaluationUrl} returned not-OK status: ${response.status} - ${response.statusText}`)
      }

      results = await response.json()
    }
  }
  catch (e) {
    console.log(e)
  }

  return results;
}

export async function focusedEvaluationFlexi(payload: any): Promise<any> {
  let results: any = {};
  try {
    if (useDeviceMRX()) {
      await mrxLoaded();
      results = await (window as any).mrx.logic.focusedEvaluationFlexi(payload)
    }
    else {
      const mrxFocusedEvaluationUrl = `${MRX_URL}/focusedEvaluationFlexi`
      const requestHeaders = {
        'Content-Type': 'application/json',
      }
      const requestBody = JSON.stringify([payload])

      const response = await fetch(mrxFocusedEvaluationUrl, {
        method: 'POST',
        headers: requestHeaders,
        body: requestBody
      })

      if (!response.ok) {
        throw new Error(`Request to ${mrxFocusedEvaluationUrl} returned not-OK status: ${response.status} - ${response.statusText}`)
      }

      results = await response.json()
    }
  }
  catch (e) {
    console.log(e)
  }

  return results;
}
